import { createFileRoute, useLocation } from '@tanstack/react-router';
import { Outlet, Link } from '@/lib/router-components';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { usePermissions } from '../hooks/usePermissions';

export const Route = createFileRoute('/w/$workspaceId/financeiro')({
  component: FinanceiroLayout,
});

const TABS = [
  { to: 'resumo', label: 'Resumo Mensal' },
  { to: 'contas-pagar', label: 'Contas a pagar' },
  { to: 'contas-receber', label: 'Contas a receber' },
  { to: 'fluxo', label: 'Fluxo de Caixa' },
  { to: 'pendencias', label: 'Pendências' },
  { to: 'previsao-sucumbencia', label: 'Previsão de sucumbência' },
  { to: 'importacao', label: 'Importação' },
  { to: 'configuracoes', label: 'Configurações' },
] as const;

function FinanceiroLayout() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const { permissions, isLoading: permsLoading } = usePermissions();
  const visibleTabs = useMemo(() => {
    const has = (k: string) => permissions.includes(k);
    return TABS.filter((tab) =>
      tab.to === 'previsao-sucumbencia' ? has('financeiro.previsao.read') : true,
    );
  }, [permissions]);
  const { data: workspace } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: !!workspaceId,
  });

  const canReadFinanceiro = permissions.includes('financeiro.read');

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50 dark:bg-background">
      <div className="mx-auto w-full max-w-7xl shrink-0 px-4 pt-6 sm:px-6 lg:px-8">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Financeiro' },
          ]}
        />
        <div
          className="mt-6 bg-white dark:bg-[#1a1a1a] rounded-[15px] p-5"
          style={{
            fontFamily: 'Poppins, sans-serif',
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="min-w-0">
            <h1 className="font-sans text-base font-normal tracking-normal text-[#06141B] dark:text-foreground">
              Financeiro
            </h1>
            <p className="mt-1 max-w-2xl font-sans text-xs font-normal text-[#9BA8AB] dark:text-muted-foreground">
              Indicadores, lançamentos e gestão operacional em um único painel.
            </p>
          </div>

          <div
            className="mt-5 pt-4"
            style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            <nav className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Seções do financeiro">
              {visibleTabs.map((tab) => {
                const href = `/w/${workspaceId}/financeiro/${tab.to}`;
                const isActive = location.pathname.startsWith(href);
                return (
                  <Link
                    key={tab.to}
                    to={`/w/$workspaceId/financeiro/${tab.to}`}
                    params={{ workspaceId }}
                    className={`whitespace-nowrap border-b-2 py-2 px-1 font-medium transition-colors ${
                      isActive
                        ? 'border-[#06141B] text-[#06141B] dark:border-[#F5F6F5] dark:text-[#F5F6F5]'
                        : 'border-transparent text-[#9BA8AB] hover:text-[#06141B] dark:hover:text-[#F5F6F5]'
                    }`}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
      <div className="mx-auto min-h-0 w-full max-w-7xl flex-1 overflow-auto px-4 py-6 sm:px-6 lg:px-8">
        {permsLoading && (
          <div className="rounded-2xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            Carregando permissões...
          </div>
        )}
        {!permsLoading && !canReadFinanceiro && (
          <div className="rounded-2xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            Você não tem permissão para acessar o módulo financeiro deste workspace. Peça a um administrador para
            incluir a permissão <span className="font-mono text-xs">financeiro.read</span> no seu papel.
          </div>
        )}
        {!permsLoading && canReadFinanceiro && <Outlet />}
      </div>
    </div>
  );
}
